import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  BankAvatar,
  Box,
  Button,
  CalendarLinkModal,
  ContractActionsMenu,
  ContractTimeline,
  Icon,
  LockedFeatureOverlay,
  ProgressBar,
  RiskChip,
  ScheduleRow,
  Screen,
  Skeleton,
  Text,
} from '~/atomic';
import type { ContractActionItem } from '~/atomic/molecules/ContractActionsMenu/view';
import { useFeature } from '~/store/entitlements/useFeature';
import { cn } from '~/utils/cn';
import modalStatusService from '~/services/modalStatus';
import { useAppDispatch, useAppSelector } from '~/store';
import {
  fetchContractDetails,
  markInstallmentPaid,
  removeContract,
} from '~/store/contracts/thunks';
import {
  selectContractById,
  selectContractProgress,
  selectInstallmentsForContract,
} from '~/store/contracts/selectors';
import {
  amortizationLabel,
  formatDateShort,
  formatRatePct,
  frequencyLabel,
  indexerLabel,
  modalityLabel,
  relativeDueLabel,
} from '~/utils/contractLabels';
import {
  formatCentsToBRL,
  formatCentsToBRLCompact,
} from '~/utils/formatFinance';

export default function ContractDetailsView() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const contract = useAppSelector(selectContractById(id ?? ''));
  const installments = useAppSelector(selectInstallmentsForContract(id ?? ''));
  const progress = useAppSelector(selectContractProgress(id ?? ''));
  const [showAllInstallments, setShowAllInstallments] = useState(false);
  const [marking, setMarking] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const { isAvailable: canUseCalendar } = useFeature('calendar_sync');

  useEffect(() => {
    if (id) dispatch(fetchContractDetails(id));
  }, [dispatch, id]);

  const sortedInstallments = useMemo(
    () =>
      [...installments].sort(
        (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
      ),
    [installments],
  );

  const openInstallmentsCount = useMemo(
    () => installments.filter((i) => i.status !== 'paid').length,
    [installments],
  );

  const lastPaidIndex = useMemo(() => {
    let last = -1;
    sortedInstallments.forEach((inst, idx) => {
      if (inst.status === 'paid') last = idx;
    });
    return last;
  }, [sortedInstallments]);

  const showBulkPayShortcut = openInstallmentsCount > 1;

  const goToBulkPay = () =>
    navigate(`/app/contracts/${id}/installments/bulk-pay`);

  const handleDelete = () => {
    modalStatusService.show({
      type: 'alert',
      icon: 'icon-trash',
      title: 'Excluir contrato?',
      description:
        'Essa ação remove o contrato e todas as parcelas associadas.',
      textFirstButton: 'Excluir',
      textSecondButton: 'Cancelar',
      showFirstButton: true,
      showSecondButton: true,
      onClickFirstButton: async () => {
        modalStatusService.hide();
        if (!id) return;
        const ok = await dispatch(removeContract(id)).unwrap();
        if (ok) navigate('/app/contracts', { replace: true });
      },
      onClickSecondButton: modalStatusService.hide,
      handleCloseModal: modalStatusService.hide,
    });
  };

  const handleMarkNextPaid = async () => {
    if (!contract || !progress?.nextInstallment) return;
    setMarking(true);
    await dispatch(
      markInstallmentPaid({
        contractId: contract.id,
        installmentId: progress.nextInstallment.id,
      }),
    );
    setMarking(false);
  };

  if (!contract || !progress) {
    return (
      <Screen headerTitle="Contrato">
        <div className="flex flex-col gap-smaller py-medium">
          <Skeleton height={160} />
          <Skeleton height={120} />
          <Skeleton height={48} />
          <Skeleton height={48} />
        </div>
      </Screen>
    );
  }

  const actions: ContractActionItem[] = [
    {
      key: 'calendar',
      label: 'Vincular ao calendário',
      description: 'Leva os vencimentos para o Google Agenda, Outlook ou Apple.',
      icon: 'icon-calendar',
      locked: !canUseCalendar,
      onClick: () => {
        setMenuOpen(false);
        if (!canUseCalendar) {
          navigate('/app/subscribe');
          return;
        }
        setCalendarOpen(true);
      },
    },
    {
      key: 'edit',
      label: 'Editar contrato',
      description: 'Ajuste valores, datas e condições.',
      icon: 'icon-pencil',
      onClick: () => {
        setMenuOpen(false);
        navigate(`/app/contracts/${contract.id}/edit`);
      },
    },
    {
      key: 'delete',
      label: 'Excluir contrato',
      description: 'Remove o contrato e todas as parcelas.',
      icon: 'icon-trash',
      destructive: true,
      onClick: () => {
        setMenuOpen(false);
        handleDelete();
      },
    },
  ];

  const next = progress.nextInstallment;
  const nextIsOverdue =
    next &&
    new Date(next.dueDate).getTime() <
      new Date(new Date().toDateString()).getTime();

  return (
    <Screen
      headerTitle={contract.institution.name}
      headerSubtitle={contract.contractNumber}
      headerRightSlot={
        <ContractActionsMenu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          actions={actions}
        >
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Ações do contrato"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="flex h-[40px] w-[40px] items-center justify-center rounded-full hover:bg-intense-02"
          >
            <Icon
              name="icon-more-vertical"
              size={22}
              color="#0E0D0D"
              rounded={false}
            />
          </button>
        </ContractActionsMenu>
      }
      hasTabNavigation
    >
      <div className="flex flex-col gap-medium py-medium">
        {next ? (
          <div
            className={cn(
              'relative overflow-hidden rounded-medium bg-intense-01 shadow-level2',
            )}
          >
            <div
              className={cn(
                'absolute left-0 top-0 h-full w-[6px]',
                nextIsOverdue ? 'bg-warning-04' : 'bg-primary-01',
              )}
            />
            <div className="p-medium pl-large">
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    'inline-flex items-center gap-quark px-smaller py-quark rounded-full',
                    nextIsOverdue ? 'bg-warning-01' : 'bg-support-01',
                  )}
                >
                  <span
                    className={cn(
                      'h-[6px] w-[6px] rounded-full',
                      nextIsOverdue ? 'bg-warning-04' : 'bg-primary-01',
                    )}
                  />
                  <Text
                    className={cn(
                      'text-captionLG uppercase tracking-wide',
                      nextIsOverdue ? 'color-warning-06' : 'color-primary-01',
                    )}
                  >
                    {nextIsOverdue ? 'PAGAMENTO ATRASADO' : 'PRÓXIMA PARCELA'}
                  </Text>
                </span>
                <RiskChip level={contract.riskLevel} compact />
              </div>

              <div className="mt-smaller flex items-start gap-small">
                <BankAvatar
                  institution={contract.institution}
                  size="lg"
                  className="shadow-level1"
                />
                <div className="flex-1 min-w-0">
                  <Text
                    className={cn(
                      'text-display1 leading-none',
                      nextIsOverdue ? 'color-warning-06' : 'color-complement-01',
                    )}
                  >
                    {formatCentsToBRL(next.amount)}
                  </Text>
                  <Text className="mt-smaller text-bodyLG color-complement-01">
                    {`Parcela ${next.index}/${contract.installmentsTotal} · ${formatDateShort(
                      next.dueDate,
                    )}`}
                  </Text>
                  <Text className="mt-quark text-bodySM color-intense-06">
                    {relativeDueLabel(next.dueDate)}
                  </Text>
                </div>
              </div>

              <div className="mt-medium flex gap-smaller">
                <Button
                  text={marking ? 'Registrando...' : 'Marcar como paga'}
                  leftIcon="icon-check"
                  onClick={handleMarkNextPaid}
                  disabled={marking}
                  fullWidth
                />
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/app/contracts/${contract.id}/installments/${next.id}`,
                    )
                  }
                  className="w-[48px] h-[48px] rounded-[14px] bg-intense-03 flex items-center justify-center hover:opacity-90 shrink-0"
                  aria-label="Detalhes da parcela"
                >
                  <Icon
                    name="icon-chevron-right"
                    size={18}
                    color="#606060"
                    rounded={false}
                  />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <Box className="rounded-medium bg-success-01 border border-success-04 p-medium">
            <div className="flex items-center gap-smaller">
              <div className="w-[40px] h-[40px] rounded-[12px] bg-success-02 flex items-center justify-center">
                <Icon
                  name="icon-circle-check"
                  size={20}
                  color="#007831"
                  rounded={false}
                />
              </div>
              <div>
                <Text className="text-paragraphLG color-complement-01">
                  Contrato quitado
                </Text>
                <Text className="text-bodySM color-intense-06">
                  Todas as parcelas foram pagas.
                </Text>
              </div>
            </div>
          </Box>
        )}

        <Box className="rounded-medium bg-intense-01 shadow-level1 p-medium flex flex-col gap-smaller">
          <div className="flex items-center justify-between">
            <Text className="text-captionLG color-intense-06 uppercase tracking-wide">
              Andamento do contrato
            </Text>
            <Text className="text-paragraphLG color-primary-01">
              {progress.percentPaid.toFixed(0)}%
            </Text>
          </div>

          <ProgressBar
            value={progress.percentPaid}
            trackClassName="h-[10px]"
            fillClassName="bg-primary-01"
          />

          <div className="mt-smaller grid grid-cols-3 gap-smaller">
            <div>
              <Text className="text-captionMD color-intense-06">Pago</Text>
              <Text className="text-bodyLG color-success-06">
                {formatCentsToBRLCompact(progress.paidAmount)}
              </Text>
            </div>
            <div>
              <Text className="text-captionMD color-intense-06">Restante</Text>
              <Text className="text-bodyLG color-complement-01">
                {formatCentsToBRLCompact(progress.remainingAmount)}
              </Text>
            </div>
            <div>
              <Text className="text-captionMD color-intense-06">Parcelas</Text>
              <Text className="text-bodyLG color-complement-01">
                {progress.installmentsPaid}/{progress.installmentsTotal}
              </Text>
            </div>
          </div>
        </Box>

        <Box className="rounded-medium bg-intense-01 shadow-level1 p-medium">
          <div className="flex items-center justify-between">
            <Text className="text-paragraphLG color-complement-01">Timeline</Text>
            <Text className="text-captionMD color-intense-06">
              {sortedInstallments.length}{' '}
              {sortedInstallments.length === 1 ? 'parcela' : 'parcelas'}
            </Text>
          </div>
          <ContractTimeline
            installments={sortedInstallments}
            activeId={next?.id}
            onSelect={(installmentId) =>
              navigate(
                `/app/contracts/${contract.id}/installments/${installmentId}`,
              )
            }
            className="mt-smaller"
          />
        </Box>

        <Box className="rounded-medium bg-intense-01 shadow-level1 p-medium flex flex-col gap-smaller">
          <Text className="text-paragraphLG color-complement-01">
            Detalhes do contrato
          </Text>
          <div className="grid grid-cols-2 gap-smaller mt-smaller">
            {[
              { label: 'Modalidade', value: modalityLabel(contract.modality) },
              {
                label: 'Amortização',
                value: amortizationLabel(contract.amortizationType),
              },
              {
                label: 'Taxa',
                value:
                  contract.indexer && contract.indexer !== 'pre-fixed'
                    ? `${indexerLabel(contract.indexer)} + ${formatRatePct(
                        contract.interestRate,
                      ).replace(' a.a.', ' p.p. a.a.')}`
                    : formatRatePct(contract.interestRate),
              },
              { label: 'Indexador', value: indexerLabel(contract.indexer) },
              {
                label: 'Periodicidade',
                value: frequencyLabel(contract.frequency),
              },
              {
                label: 'Valor financiado',
                value: formatCentsToBRLCompact(contract.amountFinanced),
              },
              { label: 'Início', value: formatDateShort(contract.startDate) },
              { label: 'Fim', value: formatDateShort(contract.endDate) },
            ].map((it) => (
              <div key={it.label}>
                <Text className="text-captionMD color-intense-06">{it.label}</Text>
                <Text className="mt-quark text-bodyMD color-complement-01">
                  {it.value}
                </Text>
              </div>
            ))}
          </div>

          {contract.guarantee || contract.cropDescription ? (
            <div className="mt-small pt-smaller border-t border-intense-03 flex flex-col gap-smaller">
              {contract.cropDescription && (
                <div>
                  <Text className="text-captionMD color-intense-06">
                    Cultura/Produto
                  </Text>
                  <Text className="mt-quark text-bodyMD color-complement-01">
                    {contract.cropDescription}
                  </Text>
                </div>
              )}
              {contract.guarantee && (
                <div>
                  <Text className="text-captionMD color-intense-06">Garantia</Text>
                  <Text className="mt-quark text-bodyMD color-complement-01">
                    {contract.guarantee}
                  </Text>
                </div>
              )}
            </div>
          ) : null}
        </Box>

        <Box className="flex flex-col gap-smaller">
          <button
            type="button"
            onClick={() => setShowAllInstallments((v) => !v)}
            className="flex items-center justify-between w-full px-small py-smaller rounded-small bg-intense-01 border border-intense-03 hover:bg-intense-02"
          >
            <Text className="text-paragraphMD color-complement-01">
              Todas as parcelas
            </Text>
            <Icon
              name={showAllInstallments ? 'icon-chevron-up' : 'icon-chevron-down'}
              size={16}
              color="#606060"
              rounded={false}
            />
          </button>
          {showAllInstallments ? (
            <LockedFeatureOverlay
              feature="payment_forecast"
              title="Previsão de pagamentos"
              description="Veja todas as parcelas futuras com vencimento, juros e amortização. Disponível nos planos pagos."
            >
              {sortedInstallments.length === 0 ? (
                <Text className="text-bodyMD color-intense-06">
                  Sem parcelas cadastradas.
                </Text>
              ) : (
                <Box className="flex flex-col gap-smaller">
                  {showBulkPayShortcut && lastPaidIndex === -1 ? (
                    <Button
                      text="Dar baixa em várias parcelas"
                      leftIcon="icon-grid"
                      variant="ghost"
                      className="border border-primary-01"
                      onClick={goToBulkPay}
                      fullWidth
                    />
                  ) : null}
                  {sortedInstallments.map((inst, idx) => (
                    <Fragment key={inst.id}>
                      <ScheduleRow
                        installment={inst}
                        contractLabel={contract.institution.name}
                        onClick={() =>
                          navigate(
                            `/app/contracts/${contract.id}/installments/${inst.id}`,
                          )
                        }
                      />
                      {showBulkPayShortcut && idx === lastPaidIndex ? (
                        <Button
                          text="Dar baixa em várias parcelas"
                          leftIcon="icon-grid"
                          variant="ghost"
                          className="border border-primary-01"
                          onClick={goToBulkPay}
                          fullWidth
                        />
                      ) : null}
                    </Fragment>
                  ))}
                </Box>
              )}
            </LockedFeatureOverlay>
          ) : null}
        </Box>

        <Box className="flex flex-col gap-smaller">
          <Button
            text="Vincular ao calendário"
            leftIcon="icon-calendar"
            variant="outline"
            onClick={() =>
              canUseCalendar
                ? setCalendarOpen(true)
                : navigate('/app/subscribe')
            }
            fullWidth
          />
          {showBulkPayShortcut ? (
            <Button
              text="Dar baixa em várias parcelas"
              leftIcon="icon-grid"
              variant="ghost"
              className="border border-primary-01"
              onClick={goToBulkPay}
              fullWidth
            />
          ) : null}
        </Box>
      </div>

      <CalendarLinkModal
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        contractId={contract.id}
        contractLabel={`${contract.institution.name} · #${contract.contractNumber}`}
      />
    </Screen>
  );
}
